"use client";

import { FC, HTMLAttributes, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { usePathname } from "next/navigation";

import DesktopHeader from "./DesktopHeader";
import MobileHeader from "./MobileHeader";
import useNavigation from "@/hooks/useNavigation";

// ----------------------------------------------------------------------------------------

export type IHeaderProps = HTMLAttributes<HTMLElement> & {};

// Headroom-style scroll behavior:
// - At the top of the page (within REVEAL_THRESHOLD), header is always visible.
// - Past that threshold, hide when scrolling down, reveal when scrolling up.
// DELTA_THRESHOLD avoids flapping on micro-scroll jitter (trackpads, scroll-anchoring).
const REVEAL_THRESHOLD = 200;
const DELTA_THRESHOLD = 8;

const Header: FC<IHeaderProps> = () => {
  const { services, industries, navigation } = useNavigation();
  const [hidden, setHidden] = useState(false);
  const lastYRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const pathname = usePathname();

  useEffect(() => {
    const onScroll = () => {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - lastYRef.current;

        if (Math.abs(delta) >= DELTA_THRESHOLD) {
          if (y < REVEAL_THRESHOLD) {
            setHidden(false);
          } else if (delta > 0) {
            setHidden(true); // scrolling down → hide
          } else {
            setHidden(false); // scrolling up → reveal
          }
          lastYRef.current = y;
        }

        rafRef.current = null;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Reset on route change: Next.js resets scrollY to 0 on navigation, so we
  // also reset our last-known position to keep direction detection consistent
  // and ensure the header is visible on the new page.
  useEffect(() => {
    lastYRef.current = 0;
    setHidden(false);
  }, [pathname]);

  return (
    <>
      <header
        className={clsx(
          "sticky top-[var(--banner-h,0px)] z-[999] w-full bg-white shadow-md transition-all duration-300 ease-in-out",
          hidden && "-translate-y-full",
        )}
      >
        <nav className="container h-[60px] xl:h-full">
          {/* Header for Desktop */}
          <DesktopHeader
            mainMenu={[services, industries, ...navigation]}
            className="hidden xl:flex"
          />

          {/* Header for Mobile */}
          <MobileHeader
            className="xl:hidden"
            mainMenu={[services, industries, ...navigation]}
          />
        </nav>
      </header>
    </>
  );
};

export default Header;
